import {
  BoundNumberField,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  IconButton,
  RowStyles,
  collapseColumn,
  column,
  emptyCell,
  numericColumn,
} from "@homebound/beam";
import { FieldState, ObjectState } from "@homebound/form-state";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { InputMaybe } from "src/generated/graphqlTypes";
import { PlanBasedBidsTable_TakeoffLineItemVersionFragment } from "src/routes/bids/components/PlanBasedBidsTable.generated";
import { EditBidContractTradeLineItemFragment } from "src/routes/bids/components/ProrateBidModal.generated";
import { DropCodeCell } from "src/routes/developments/product-offerings/components/DropCodeCell";
import { fail, groupBy, prorate, roundToDigits } from "src/utils";
import { getOptionsKey } from "../BidsTab";
import { FormValues, ProrationFormValues } from "../ProrateBidModal";
import { ProrationCostColumn, ProrationUnitCostColumn } from "./ProrationCostColumn";

export function initializeMaterialFormState(
  scopeLineItems: PlanBasedBidsTable_TakeoffLineItemVersionFragment[],
  existingTradeLineItems: EditBidContractTradeLineItemFragment[],
): FormValues {
  const materialItemTemplateItems = scopeLineItems.filter((tli) => !!tli.bidItem?.parentMaterialVariant);

  const materialLaborEstimateMap = prorateLaborEstimatesForMaterials(scopeLineItems);

  const groupedTLIs = groupBy(
    materialItemTemplateItems,
    (tli) => tli.readyPlan?.id + getOptionsKey(tli.options) + tli.bidItem?.id,
  );

  return {
    bidContractTradeLineItems: Object.entries(groupedTLIs).map(([key, materialTlis]) => {
      // The existing trade line item would match the product offering, options, and bid item
      const existingTradeLineItem = existingTradeLineItems.find(
        (tli) => tli.productOffering.id + getOptionsKey(tli.options) + tli.bidItem?.id === key,
      );

      const quantity = materialTlis.sum((tli) => tli.quantity || 0) || 1;

      const prorations = [];
      for (const materialTli of materialTlis) {
        const laborLine = scopeLineItems.find((allTli) => allTli.identity.id === materialTli.laborLine?.id);

        const existingMaterialProration = existingTradeLineItem?.prorations.find(
          (p) => p.lineItem.itemTemplateItem?.id === materialTli.id,
        );

        prorations.push({
          id: existingMaterialProration?.id,
          quantity: materialTli.quantity,
          unitCostInCents: existingMaterialProration
            ? roundToDigits(existingMaterialProration.costInCents / (materialTli.quantity || 1), 1)
            : undefined,
          costInCents: existingMaterialProration?.costInCents,
          estimatedCostInCents: materialTli.costEstimate.costInCents,

          itemTemplateItemId: materialTli.id,
          userEntered: !!existingMaterialProration?.userEntered,
        });

        if (laborLine) {
          // If the labor line has already been accounted for in this grouping just add the quantity and estimated cost
          const existingLaborFormState = prorations.find((p) => p.itemTemplateItemId === laborLine?.id);
          if (existingLaborFormState) {
            if (materialTli.quantity) {
              existingLaborFormState.quantity = (existingLaborFormState.quantity || 0) + materialTli.quantity;
            }
            existingLaborFormState.estimatedCostInCents += materialLaborEstimateMap.get(
              `${laborLine.identity.id}${materialTli.id}`,
            )!;
          } else {
            const existingLaborProration = existingTradeLineItem?.prorations.find(
              (p) => p.lineItem.itemTemplateItem?.id === laborLine.id,
            );
            prorations.push({
              id: existingLaborProration?.id,
              quantity: materialTli.quantity,
              unitCostInCents: existingLaborProration
                ? roundToDigits(existingLaborProration.costInCents / (materialTli.quantity || 1), 1)
                : undefined,
              costInCents: existingLaborProration?.costInCents,
              estimatedCostInCents: materialLaborEstimateMap.get(`${laborLine.identity.id}${materialTli.id}`)!,

              itemTemplateItemId: laborLine.id,
              userEntered: !!existingLaborProration?.userEntered,
            });
          }
        }
      }

      return {
        id: existingTradeLineItem?.id,
        quantity,
        unitCostInCents: existingTradeLineItem
          ? roundToDigits(existingTradeLineItem.totalCostInCents / quantity, 1)
          : undefined,
        totalCostInCents: existingTradeLineItem?.totalCostInCents,

        productOfferingId: materialTlis[0].readyPlan!.id,
        optionIds: materialTlis[0].options.sortByKey("id").map((o) => o.id),
        bidItemId: materialTlis[0].bidItem?.id,
        prorations,
      };
    }),
  };
}

/**
 * Because a labor tli maybe be used by multiple material tlis, we need to split out the labor tli estimated cost.
 * To do this we look at the cost estimate of each of the materials and ratio of the cost estimates to split out the labor cost.
 */
function prorateLaborEstimatesForMaterials(
  scopeLineItems: PlanBasedBidsTable_TakeoffLineItemVersionFragment[],
): Map<string, number> {
  const materialItemTemplateItems = scopeLineItems.filter((tli) => !!tli.bidItem?.parentMaterialVariant);
  // Group the material TLIs by the labor TLI
  const groupByLaborTLI = materialItemTemplateItems
    .filter((tli) => tli.laborLine)
    .groupBy((tli) => tli.laborLine?.id || "");
  // the key will be a combination of the labor TLI and the material TLI id
  const materialLaborEstimateMap = new Map<string, number>();
  Object.entries(groupByLaborTLI).forEach(([key, materialTlis]) => {
    const laborLine = scopeLineItems.find((allTli) => allTli.identity.id === key);
    if (!laborLine) {
      return;
    }

    // Calculate the total cost of all materials
    const materialTotal = materialTlis.sum((tli) => tli.costEstimate.costInCents);
    // Calculate the ratios by dividing the materials by the material total
    const ratios = materialTlis.map((tli) => tli.costEstimate.costInCents / materialTotal);
    const proratedCosts = prorate(laborLine.costEstimate.costInCents, ratios);
    // Write the cost estimates back to the map
    for (let index = 0; index < materialTlis.length; ++index) {
      materialLaborEstimateMap.set(`${key}${materialTlis[index].id}`, proratedCosts[index]);
    }
  });

  return materialLaborEstimateMap;
}

type MaterialTablesProps = {
  formState: ObjectState<FormValues>;
  scopeLineItems: PlanBasedBidsTable_TakeoffLineItemVersionFragment[];
  tradePartnerName?: string;
};

const threeDigitCurrencyFormat = { style: "currency" as const, currency: "USD", minimumFractionDigits: 3 };

export function MaterialTables({ formState, scopeLineItems, tradePartnerName }: MaterialTablesProps) {
  const productOfferings = scopeLineItems.map((tli) => tli.readyPlan!).unique();
  const columns = createColumns();
  return (
    <div css={Css.df.fdc.gap3.w100.$}>
      {productOfferings.map((po) => {
        const rows = createRows(po, formState, scopeLineItems, tradePartnerName);
        return <GridTable key={po.id} columns={columns} rows={rows} rowStyles={rowStyles} />;
      })}
    </div>
  );
}

const rowStyles: RowStyles<Row> = {
  productOffering: {
    cellCss: Css.py3.$,
  },
  head: {
    cellCss: Css.sm.$,
  },
};

function createRows(
  productOffering: NamedFragment,
  formState: ObjectState<FormValues>,
  scopeLineItems: PlanBasedBidsTable_TakeoffLineItemVersionFragment[],
  tradePartnerName?: string,
): GridDataRow<Row>[] {
  const materialItemTemplateItems = scopeLineItems.filter(
    (tli) => tli.readyPlan!.id === productOffering.id && !!tli.bidItem?.parentMaterialVariant,
  );

  const optionGroupBy = groupBy(materialItemTemplateItems, (tli) => getOptionsKey(tli.options));
  const optionRows = Object.keys(optionGroupBy).map((optionKey) => {
    const children = optionGroupBy[optionKey];
    const groupedChildren = groupBy(children, (tli) => tli.bidItem!.id);

    return {
      kind: "option" as const,
      id: optionKey || "base",
      data: {
        options: children[0].options,
      },
      children: Object.entries(groupedChildren).flatMap(([key, materialTlis]) => {
        const bidContractTradeLineItem = formState.bidContractTradeLineItems.rows.find(
          (bcli) =>
            bcli.productOfferingId.value === productOffering.id &&
            (bcli.optionIds.value || []).join(",") === optionKey &&
            bcli.bidItemId.value === materialTlis[0].bidItem?.id,
        );
        if (!bidContractTradeLineItem) {
          fail("BidContractTradeLineItem not found for product offering, options, and bid item");
        }

        const prorations = [];
        for (const materialTli of materialTlis) {
          const laborLine = scopeLineItems.find((allTli) => allTli.identity.id === materialTli.laborLine?.id);

          const materialFormState = bidContractTradeLineItem.prorations.rows.find(
            (p) => p.itemTemplateItemId.value === materialTli.id,
          );
          const laborFormState = laborLine
            ? bidContractTradeLineItem.prorations.rows.find((p) => p.itemTemplateItemId.value === laborLine.id)
            : undefined;

          if (!materialFormState) {
            fail("materialFormState not found for item template item");
          }

          prorations.push({
            kind: "materialProration" as const,
            id: materialTli.id,
            data: {
              itemTemplateItem: materialTli,
              prorationState: materialFormState,
            },
          });

          if (laborLine && laborFormState) {
            const existingLaborLine = prorations.find((p) => p.data.itemTemplateItem.id === laborLine.id);
            // If the labor line has already been accounted for in this grouping skip it
            if (!existingLaborLine) {
              prorations.push({
                kind: "laborProration" as const,
                id: `${materialTli.id}-${laborLine.id}`,
                data: {
                  materialItemTemplateItem: materialTli,
                  itemTemplateItem: laborLine,
                  prorationState: laborFormState,
                },
              });
            }
          }
        }

        return {
          kind: "material" as const,
          id: `material-${materialTlis[0].id}`,
          data: {
            itemTemplateItem: materialTlis[0],
            totalCostField: bidContractTradeLineItem.totalCostInCents,
            unitCostField: bidContractTradeLineItem.unitCostInCents,
          },
          children: prorations,
        };
      }),
    };
  });

  return [
    {
      kind: "productOffering" as const,
      id: "productOffering",
      data: {
        productOfferingName: productOffering.name,
        tradePartnerName,
      },
      pin: "first",
    },
    { kind: "head" as const, id: "head", data: undefined, pin: "last" },
    ...optionRows,
  ];
}

type ProductOfferingRow = {
  kind: "productOffering";
  data: {
    productOfferingName: string;
    tradePartnerName?: string;
  };
};
type HeaderRow = { kind: "head"; data: undefined };
type OptionRow = { kind: "option"; data: { options: NamedFragment[] } };
type MaterialRow = {
  kind: "material";
  data: {
    itemTemplateItem: PlanBasedBidsTable_TakeoffLineItemVersionFragment;
    totalCostField: FieldState<InputMaybe<number>>;
    unitCostField: FieldState<InputMaybe<number>>;
  };
};
type MaterialProrationRow = {
  kind: "materialProration";
  data: {
    itemTemplateItem: PlanBasedBidsTable_TakeoffLineItemVersionFragment;
    prorationState: ObjectState<ProrationFormValues>;
  };
};
type LaborProrationRow = {
  kind: "laborProration";
  data: {
    materialItemTemplateItem: PlanBasedBidsTable_TakeoffLineItemVersionFragment;
    itemTemplateItem: PlanBasedBidsTable_TakeoffLineItemVersionFragment;
    prorationState: ObjectState<ProrationFormValues>;
  };
};

type Row = HeaderRow | ProductOfferingRow | OptionRow | MaterialRow | MaterialProrationRow | LaborProrationRow;

function createColumns(): GridColumn<Row>[] {
  return [
    collapseColumn<Row>({
      productOffering: ({ productOfferingName, tradePartnerName }) => ({
        content: (
          <div css={Css.df.w100.aic.$}>
            <div css={Css.xl.$}>
              {productOfferingName} {tradePartnerName && <span css={Css.gray600.$}>{`— ${tradePartnerName} Bid`}</span>}
            </div>
          </div>
        ),
        colspan: 9,
      }),
      // This functions as a header/parent toggle to collapse/expand all option rows. It needs to be hand written because we don't have a true header row kind.
      head: (data, { api }) => {
        const collapsed = api.getVisibleRows("option").every((r) => api.isCollapsedRow(r.id));
        return (
          <IconButton
            onClick={() =>
              api.getVisibleRows("option").forEach((r) => {
                if (collapsed && api.isCollapsedRow(r.id)) {
                  api.toggleCollapsedRow(r.id);
                } else if (!collapsed && !api.isCollapsedRow(r.id)) {
                  api.toggleCollapsedRow(r.id);
                }
              })
            }
            icon={collapsed ? "chevronRight" : "chevronDown"}
          />
        );
      },
    }),
    column<Row>({
      productOffering: emptyCell,
      head: "Code",
      option: emptyCell,
      material: emptyCell,
      materialProration: ({ itemTemplateItem }) => <DropCodeCell scope={itemTemplateItem} />,
      laborProration: ({ itemTemplateItem }) => <DropCodeCell scope={itemTemplateItem} />,
      w: "100px",
    }),
    column<Row>({
      productOffering: emptyCell,
      head: "Name",
      option: ({ options }) => ({ content: options.map((o) => o.name).join(", ") || "Base", css: Css.smSb.$ }),
      material: ({ itemTemplateItem }) => ({ content: itemTemplateItem.name, css: Css.xsSb.$ }),
      materialProration: ({ itemTemplateItem }) => itemTemplateItem.name,
      laborProration: ({ itemTemplateItem }) => itemTemplateItem.name,
    }),
    column<Row>({
      productOffering: emptyCell,
      head: "Material Code",
      option: emptyCell,
      material: emptyCell,
      materialProration: ({ itemTemplateItem }) => itemTemplateItem.bidItem?.parentMaterialVariant?.code,
      laborProration: ({ itemTemplateItem }) => itemTemplateItem.bidItem?.parentMaterialVariant?.code,
      // w: "120px", are the material codes going to be this long?
    }),
    column<Row>({
      productOffering: emptyCell,
      head: "Qty",
      option: emptyCell,
      material: emptyCell,
      materialProration: ({ itemTemplateItem }) => itemTemplateItem.quantity,
      laborProration: ({ prorationState }) => prorationState.quantity.value,
      w: "53px",
    }),
    column<Row>({
      productOffering: emptyCell,
      head: "UoM",
      option: emptyCell,
      material: emptyCell,
      materialProration: ({ itemTemplateItem }) => itemTemplateItem.unitOfMeasure.shortName,
      laborProration: ({ materialItemTemplateItem }) => materialItemTemplateItem.unitOfMeasure.shortName,
      w: "67px",
    }),
    numericColumn<Row>({
      productOffering: emptyCell,
      head: "Cost",
      option: emptyCell,
      material: ({ unitCostField }) => (
        <BoundNumberField numberFormatOptions={threeDigitCurrencyFormat} numFractionDigits={3} field={unitCostField} />
      ),
      materialProration: ProrationUnitCostColumn,
      laborProration: ProrationUnitCostColumn,
      w: "160px",
    }),
    numericColumn<Row>({
      productOffering: emptyCell,
      head: "Total Cost",
      option: emptyCell,
      material: ({ totalCostField }) => <BoundNumberField field={totalCostField} />,
      materialProration: ProrationCostColumn,
      laborProration: ProrationCostColumn,
      w: "160px",
    }),
  ];
}
